import { LIST_LIMIT_DEFAULT, LIST_LIMIT_MAX } from '../constants/network';

// In-memory stand-in for Core. It only answers routes that exist in the
// Core OpenAPI contract (docs/core-contract.md) and mirrors its envelopes:
// lists are { items, pagination: { total, limit, offset } }, errors are
// { statusCode, error, message, fields?, gate? }.

const reply = (status, data) => ({ status, data });

function error(status, message, extra = {}) {
    const names = { 400: 'Bad Request', 403: 'Forbidden', 404: 'Not Found', 409: 'Conflict', 422: 'Unprocessable Entity' };
    return reply(status, { statusCode: status, error: names[status] || 'Error', message, ...extra });
}

function toInt(value, fallback) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
}

function paginate(items, query) {
    const limit = Math.min(Math.max(toInt(query.limit, LIST_LIMIT_DEFAULT), 1), LIST_LIMIT_MAX);
    const offset = Math.max(toInt(query.offset, 0), 0);
    return reply(200, {
        items: items.slice(offset, offset + limit),
        pagination: { total: items.length, limit, offset },
    });
}

function matchesQuery(text, query) {
    if (!query) return true;
    return String(text || '').toLocaleLowerCase('es').includes(String(query).toLocaleLowerCase('es').trim());
}

function byNumber(field, query) {
    if (query == null || query === '') return () => true;
    const expected = Number(query);
    return (row) => row[field] === expected;
}

export function createDemoServer(db, { now = () => Date.now() } = {}) {
    const projectOf = (task) => task.project_id
        ?? db.tiers.find((tier) => tier.id === task.tier_id)?.project_id
        ?? db.repositories?.find((repository) => repository.id === task.repository_id)?.project_id
        ?? null;
    const withProject = (task) => ({ ...task, project_id: projectOf(task) });
    let nextEventId = 10_000;

    const routes = [
        ['get', /^auth\/me$/, () => reply(200, db.me)],
        ['post', /^auth\/logout$/, () => reply(201, { ok: true })],

        ['get', /^admin\/projects$/, (_params, query) => paginate(
            db.projects
                .filter((project) => matchesQuery(`${project.name} ${project.slug} ${project.description}`, query.q))
                .filter((project) => !query.project_state || project.project_state === query.project_state)
                .filter(byNumber('client_id', query.client_id)),
            query,
        )],
        ['get', /^admin\/projects\/(\d+)$/, ([id]) => {
            const project = db.projects.find((row) => row.id === Number(id));
            if (!project) return error(404, 'Proyecto no encontrado');
            const teamIds = db.projectTeams.filter((link) => link.project_id === project.id).map((link) => link.team_id);
            return reply(200, {
                ...project,
                client: db.clients.find((client) => client.id === project.client_id) || null,
                repositories: db.repositories.filter((repo) => repo.project_id === project.id),
                tiers: db.tiers.filter((tier) => tier.project_id === project.id),
                teams: db.teams.filter((team) => teamIds.includes(team.id)),
                directives: [],
            });
        }],
        ['get', /^admin\/plans$/, (_params, query) => paginate(db.plans.filter(byNumber('project_id', query.project_id)), query)],
        ['get', /^admin\/repositories$/, (_params, query) => paginate(
            db.repositories
                .filter(byNumber('project_id', query.project_id))
                .filter((repo) => !query.kind || repo.kind === query.kind),
            query,
        )],
        ['get', /^admin\/tiers$/, (_params, query) => paginate(
            db.tiers
                .filter(byNumber('project_id', query.project_id))
                .filter((tier) => !query.tier_state || tier.tier_state === query.tier_state),
            query,
        )],

        ['get', /^admin\/tasks$/, (_params, query) => paginate(
            db.tasks
                .map(withProject)
                .filter(byNumber('project_id', query.project_id))
                .filter(byNumber('tier_id', query.tier_id))
                .filter(byNumber('repository_id', query.repository_id))
                .filter((task) => !query.workflow_state || task.workflow_state === query.workflow_state)
                .filter((task) => !query.kind || task.kind === query.kind)
                .sort((a, b) => b.priority - a.priority || a.id - b.id),
            query,
        )],
        ['get', /^admin\/tasks\/(\d+)$/, ([id]) => {
            const task = db.tasks.find((row) => row.id === Number(id));
            if (!task) return error(404, 'Tarea no encontrada');
            const rounds = db.reviewRounds.filter((round) => round.task_id === task.id);
            return reply(200, {
                ...withProject(task),
                tier: db.tiers.find((tier) => tier.id === task.tier_id) || null,
                repository: db.repositories.find((repo) => repo.id === task.repository_id) || null,
                reviews: rounds.filter((round) => round.decision).map((round) => ({ round: round.number, ...round.decision })),
                claimed: db.seats.find((seat) => seat.id === task.claimed_by && seat.work.task?.id === task.id)?.label || null,
            });
        }],
        ['get', /^admin\/tasks\/(\d+)\/review-rounds$/, ([id], query) => {
            const task = db.tasks.find((row) => row.id === Number(id));
            if (!task) return error(404, 'Tarea no encontrada');
            const rounds = db.reviewRounds
                .filter((round) => round.task_id === task.id)
                .sort((a, b) => b.number - a.number);
            return paginate(rounds, query);
        }],
        ['post', /^admin\/tasks\/(\d+)\/review-round\/decision$/, ([id], _query, body) => decide(Number(id), body)],

        ['get', /^admin\/teams$/, (_params, query) => paginate(
            db.teams.filter((team) => matchesQuery(`${team.name} ${team.description}`, query.q)),
            query,
        )],
        ['get', /^admin\/teams\/(\d+)$/, ([id]) => {
            const team = db.teams.find((row) => row.id === Number(id));
            return team ? reply(200, team) : error(404, 'Equipo no encontrado');
        }],
        ['get', /^admin\/teams\/(\d+)\/members$/, ([id], query) => {
            if (!db.teams.some((row) => row.id === Number(id))) return error(404, 'Equipo no encontrado');
            return paginate(db.members.filter((member) => member.team_id === Number(id)), query);
        }],
        ['get', /^admin\/project-teams$/, (_params, query) => paginate(
            db.projectTeams
                .filter(byNumber('team_id', query.team_id))
                .filter(byNumber('project_id', query.project_id)),
            query,
        )],
        ['get', /^admin\/coordination\/seats$/, (_params, query) => paginate(
            db.seats.filter(byNumber('repository_id', query.repository_id)),
            query,
        )],
        ['get', /^admin\/events$/, (_params, query) => paginate(
            db.events
                .filter(byNumber('project_id', query.project_id))
                .filter(byNumber('task_id', query.task_id))
                .filter((event) => !query.kind || event.kind === query.kind)
                .sort((a, b) => b.at.localeCompare(a.at)),
            query,
        )],
        ['get', /^admin\/attention$/, (_params, query) => paginate(
            db.attention.filter((item) => !query.kind || item.kind === query.kind),
            query,
        )],
    ];

    function decide(taskId, body) {
        const task = db.tasks.find((row) => row.id === taskId);
        if (!task) return error(404, 'Tarea no encontrada');
        if (!body || typeof body !== 'object') return error(400, 'Cuerpo inválido');
        const unknown = Object.keys(body).filter((key) => !['review_round_id', 'result', 'reason'].includes(key));
        if (unknown.length) return error(422, `Campos no permitidos: ${unknown.join(', ')}`);
        if (!['approved', 'returned'].includes(body.result)) {
            return error(400, 'result debe ser approved o returned', { fields: { result: 'Valor no permitido' } });
        }
        const reason = typeof body.reason === 'string' ? body.reason.trim() : '';
        if (body.result === 'returned' && !reason) {
            return error(422, 'Devolver exige un motivo', { fields: { reason: 'Indica por qué se devuelve' } });
        }
        const open = db.reviewRounds.find((round) => round.task_id === taskId && round.state === 'open');
        if (!open || open.id !== Number(body.review_round_id)) {
            return error(409, 'La ronda ya no es la vigente', { gate: 'ronda', current_round: open?.id ?? null });
        }
        if (!open.versioned && body.result === 'approved') {
            return error(422, 'La ronda no está versionada', { gate: 'version' });
        }

        const decidedAt = new Date(now()).toISOString();
        open.state = body.result;
        open.decision = { result: body.result, reason: reason || null, decided_at: decidedAt, decided_by: 'Sesión de demostración' };
        task.workflow_state = body.result === 'approved' ? 'done' : 'returned';
        task.updated_at = decidedAt;
        if (body.result === 'returned') task.last_return = { reason, at: decidedAt };
        db.attention = db.attention.filter((item) => !(item.resource.type === 'task' && item.resource.id === taskId));
        nextEventId += 1;
        db.events.unshift({
            id: nextEventId,
            at: decidedAt,
            kind: 'review.decided',
            summary: `${body.result === 'approved' ? 'Ronda aprobada' : 'Ronda devuelta'} (demo): «${task.title}».`,
            project_id: projectOf(task),
            tier_id: task.tier_id,
            task_id: task.id,
            repository_id: task.repository_id,
            actor: 'Sesión de demostración',
        });

        return reply(201, {
            effect: {
                stage: 'decision_aplicada',
                applied: {
                    workflow_state: task.workflow_state,
                    reservation_released: true,
                    assigned_before: task.assigned_to,
                    assigned_to: task.assigned_to,
                    assigned_reason: task.assigned_to ? 'responsable_vigente' : 'sin_responsable',
                },
            },
        });
    }

    function handle(method, path, query = {}, body = undefined) {
        const cleanPath = String(path).replace(/^\/+/, '').replace(/\?.*$/, '').replace(/\/+$/, '');
        for (const [routeMethod, pattern, handler] of routes) {
            if (routeMethod !== method) continue;
            const match = cleanPath.match(pattern);
            if (match) return handler(match.slice(1), query || {}, body);
        }
        return error(404, `Ruta de demostración no disponible: ${method.toUpperCase()} ${cleanPath}`);
    }

    return { handle };
}
